import React, { useEffect, useState, useMemo } from "react";
import SettingsClient from "util/SettingsClient";
import { useLocalStorage, useEffectOnce } from "react-use";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { actions, selectors } from "rdx";
import { Helmet } from "react-helmet";
import { ZdChatShow, ZdChatHide } from "components/Icons";
import { logToAirbrake } from "config/airbrake/log-to-airbrake";
import { generateErrorUID } from "util/generate-error-uid";
import SessionClient from "util/SessionClient";
import { browserHistory } from "rdx/configureStore";
import useToggleZendeskChatbot from "hooks/useToggleZendeskChatbot";

const DEV_URL = "https://static.zdassets.com/ekr/snippet.js?key=78e1e152-61f1-4549-8ef7-b90e2edcf82c";
const PROD_URL = "https://static.zdassets.com/ekr/snippet.js?key=d04a5ffe-39e7-4877-8043-3f1e24deae48";

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: 34,
  height: 34,
  padding: 0,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: theme.semantic.cInputBg,
  border: `1px solid ${theme.semantic.cInputBorder}`,
  cursor: "pointer",
  color: theme.semantic.cTextMuted,
  transition: `background ${theme.semantic.motionFast}, color ${theme.semantic.motionFast}`,
  "&:hover": {
    background: theme.semantic.cInputHoverBg,
    color: theme.semantic.cTextStrong,
  },
}));

const InjectZendeskWidget = ({ displayWidget }) => {
  const isProductionEnv = process.env.REACT_APP_ENV === "production";
  const settings = useMemo(() => {
    return new SettingsClient();
  }, []);
  const sessionClient = useMemo(() => {
    return new SessionClient();
  }, []);

  const { widget_show: widgetShow, widget_try_auth: widgetTryAuth } = settings.data?.zendesk || {
    widget_show: false,
    widget_try_auth: false,
  };
  const [localDisplayZendeskWidget, setLocalDisplayZendeskWidget] = useLocalStorage("displayZendeskWidget", false);
  const [zdLauncherLabelRemoved, setZdLauncherLabelRemoved] = useLocalStorage("ZD-launcherLabelRemoved", true);
  const zendeskToken = useSelector(selectors.selectZendeskToken);
  const isZendeskAuthenticated = useSelector(selectors.selectIsZendeskAuthenticated);
  const { toggleChatbot, isChatbotOpen } = useToggleZendeskChatbot();
  const [helmetState, setHelmetState] = useState(0);
  const dispatch = useDispatch();

  // HIDE LAUNCHER LABEL BECAUSE IT KEEPS COMING BACK:
  useEffect(() => {
    setZdLauncherLabelRemoved(true);
  }, [zdLauncherLabelRemoved, setZdLauncherLabelRemoved, displayWidget]);

  useEffectOnce(() => {
    setZdLauncherLabelRemoved(true);
    dispatch(actions.setDisplayZendeskWidget(!!localDisplayZendeskWidget));
    setLocalDisplayZendeskWidget(!!localDisplayZendeskWidget);
  });

  useEffect(() => {
    setZdLauncherLabelRemoved(true);
    if (displayWidget === true) {
      setLocalDisplayZendeskWidget(!!displayWidget);
    } else {
      setLocalDisplayZendeskWidget(false);
    }
  }, [displayWidget, setLocalDisplayZendeskWidget, setZdLauncherLabelRemoved]);

  // AUTHENTICATION:
  useEffect(() => {
    // Adding delay to ensure widget's window.zE function is loaded.
    const timeout = setTimeout(() => {
      if (window?.zE && zendeskToken && helmetState > 0 && !isZendeskAuthenticated && widgetTryAuth) {
        window.zE(
          "messenger",
          "loginUser",
          function jwtCallback(callback) {
            callback(zendeskToken);
            dispatch(actions.setIsZendeskAuthenticated(true));
            console.info("Authenticating user into zendesk messenger"); // eslint-disable-line no-console
          },

          function loginCallback(error) {
            if (error) {
              const errorUID = generateErrorUID();
              const { type, reason, message } = error;
              const info = { component: "InjectZendeskWidget", function: "loginCallback", type, reason, message };
              const {
                user: {
                  props: {
                    email,
                    id,
                    authFormat,
                    certifications,
                    displayRoles,
                    enrollment,
                    enterpriseId,
                    firstName,
                    lastName,
                    rolesseatAssignments,
                    seatId,
                    statusFlags,
                  },
                },
              } = sessionClient;

              logToAirbrake({
                error,
                info,
                errorUID,
                session: {
                  email,
                  id,
                  authFormat,
                  certifications,
                  displayRoles,
                  enrollment,
                  enterpriseId,
                  firstName,
                  lastName,
                  rolesseatAssignments,
                  seatId,
                  statusFlags,
                },
                browserHistory,
              });
              console.warn("Error Authenticating user into zendesk messenger"); // eslint-disable-line no-console
            }
          },
        );
      }
    }, 2000);
    return () => clearTimeout(timeout);
  }, [helmetState, zendeskToken, isZendeskAuthenticated, widgetTryAuth, sessionClient, dispatch]);

  useEffect(() => {
    if (window?.zE) {
      const handleClose = () => {
        dispatch(actions.setIsZendeskChatbotOpen(false));
        window.zE("messenger", "hide");
      };

      window.zE("messenger:on", "close", handleClose);
    }
  }, [window?.zE]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isZendeskAuthenticated && isChatbotOpen) {
      toggleChatbot(true);
    } else {
      toggleChatbot(false);
    }
  }, [isZendeskAuthenticated, isChatbotOpen, toggleChatbot]);

  // if (window?.zE) {
  // TEMP DISABLE AUTO OPEN ON NEW MESSAGE:
  // window.zE("messenger:on", "unreadMessages", (count) => {
  //   if (count > 0) {
  //     setShowWidget(true);
  //   }
  // });

  return widgetShow ? (
    <>
      <Helmet onChangeClientState={(newState) => setHelmetState(newState?.scriptTags?.length)}>
        <script id="ze-snippet" src={isProductionEnv ? PROD_URL : DEV_URL} />
        <script type="text/javascript" />
      </Helmet>
      <StyledIconButton aria-label="toggle chatbot" disableRipple onClick={() => toggleChatbot(!isChatbotOpen)}>
        {isChatbotOpen ? (
          <ZdChatHide width="26" height="26" fill="transparent" stroke="currentColor" />
        ) : (
          <ZdChatShow width="26" height="26" fill="transparent" stroke="currentColor" />
        )}
      </StyledIconButton>
    </>
  ) : null;
};

export default InjectZendeskWidget;

// https://developer.zendesk.com/api-reference/widget-messaging/web/core/
// https://developer.zendesk.com/api-reference/widget-messaging/web/authentication/
// https://support.zendesk.com/hc/en-us/articles/4500747797914-Configuring-the-name-and-appearance-of-the-Web-Widget
